import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Text,
    Alert,
    useMantineTheme,
    Select,
    SegmentedControl,
    Tooltip,
    Group,
    Stack
} from '@mantine/core';
import { IconBinaryTree, IconTable } from '@tabler/icons-react';
import { getReferenceDisplay } from '../../types/ReferenceTypes';
import { debugVerboseLog } from '../../utils/debugLog';
import { LoadingState } from './LoadingState';

// Import Cytoscape component with proper error handling
let CytoscapeComponent: any = null;
try {
    const CytoscapeComponentModule = require('react-cytoscapejs');
    CytoscapeComponent = CytoscapeComponentModule.default;
    debugVerboseLog('🔍 ReferencesGraph: Cytoscape component loaded successfully');
} catch (error) {
    console.warn('🔍 ReferencesGraph: Failed to load Cytoscape component:', error);
    CytoscapeComponent = null;
}

// Helper function to safely display entity types
const formatEntityType = (entityType: any): string => {
    if (!entityType) return 'Unknown';
    if (typeof entityType === 'string') return entityType;
    if (typeof entityType === 'object') {
        // Try to extract meaningful information from object
        // Prioritize EntityType property if it exists
        if (entityType.EntityType) return String(entityType.EntityType);
        if (entityType.$type) return entityType.$type;
        if (entityType.type) return entityType.type;
        if (entityType.name) return entityType.name;
        if (entityType.value) return entityType.value;
        // If it's a complex object, show a simplified version
        return `Object (${Object.keys(entityType).join(', ')})`;
    }
    return String(entityType);
};

// Enhanced fallback component with detailed relationship information
const FallbackGraph = ({ nodes, edges }: { nodes: any[], edges: any[] }) => {
    const theme = useMantineTheme();
    const mainEntity = nodes.find(node => node.data.isMainEntity) || nodes[0];
    const referenceNodes = nodes.filter(node => !node.data.isMainEntity);

    return (
        <Stack
            h="100%"
            bg="var(--mantine-color-default)"
            p="md"
            gap="md"
            style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: theme.radius.md
            }}
        >
            <Text size="lg" fw={600} c="blue" ta="center" mb="sm">
                🔗 Entity Relationships
            </Text>

            <Stack flex={1} style={{ overflow: 'auto' }} gap="md">
                {/* Main Entity */}
                {mainEntity && (
                    <Box
                        p="md"
                        bg="var(--mantine-color-blue-1)"
                        style={{
                            borderRadius: theme.radius.sm,
                            border: '1px solid var(--mantine-color-blue-3)'
                        }}
                    >
                        <Text size="sm" fw={600} c="blue" mb="xs">
                            📍 Main Entity
                        </Text>
                        <Text size="sm" fw={600}>
                            {String(mainEntity.data.label || 'Unknown Entity')}
                        </Text>
                        <Text size="xs" c="dimmed">
                            Type: {formatEntityType(mainEntity.data.entityType)}
                        </Text>
                    </Box>
                )}

                {/* References */}
                {referenceNodes.length > 0 && (
                    <Stack flex={1}>
                        <Text size="sm" fw={600} c="dimmed" mb="xs">
                            🔗 References ({referenceNodes.length})
                        </Text>
                        <Box
                            flex={1}
                            style={{
                                overflow: 'auto',
                                minHeight: 150,
                                maxHeight: 'calc(100vh - 500px)'
                            }}
                        >
                            {referenceNodes.map((node, index) => (
                                <Box
                                    key={index}
                                    p="xs"
                                    mb="xs"
                                    bg="var(--mantine-color-green-1)"
                                    style={{
                                        borderRadius: theme.radius.xs,
                                        border: '1px solid var(--mantine-color-green-3)'
                                    }}
                                >
                                    <Text size="sm" fw={500}>
                                        {String(node.data.label || 'Unknown Reference')}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        Type: {formatEntityType(node.data.entityType)}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                )}

                {/* Relationships */}
                {edges.length > 0 && (
                    <Stack flex={1}>
                        <Text size="sm" fw={600} c="dimmed" mb="xs">
                            🔗 Relationships ({edges.length})
                        </Text>
                        <Box
                            flex={1}
                            style={{
                                overflow: 'auto',
                                minHeight: 200,
                                maxHeight: 'calc(100vh - 400px)'
                            }}
                        >
                            {edges.map((edge, index) => (
                                <Box
                                    key={index}
                                    p="xs"
                                    mb="xs"
                                    bg="var(--mantine-color-yellow-1)"
                                    style={{
                                        borderRadius: theme.radius.xs,
                                        border: '1px solid var(--mantine-color-yellow-3)'
                                    }}
                                >
                                    <Text size="sm">
                                        <strong>{String(edge.data.label || 'Unknown')}</strong> relationship
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {String(edge.data.source || 'Unknown')} → {String(edge.data.target || 'Unknown')}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export interface ReferencesGraphProps {
    entity: any | null;
}

interface GraphNode {
    data: {
        id: string;
        label: string;
        type: string;
        entityType?: string;
        isMainEntity?: boolean;
    };
    position?: {
        x: number;
        y: number;
    };
}

interface GraphEdge {
    data: {
        id: string;
        source: string;
        target: string;
        label: string;
        relationship: string;
    };
}

export const ReferencesGraph: React.FC<ReferencesGraphProps> = ({ entity }) => {
    const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; edges: GraphEdge[] }>({ nodes: [], edges: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedReferenceProperty, setSelectedReferenceProperty] = useState<string>('all');
    const [availableReferenceProperties, setAvailableReferenceProperties] = useState<Array<{ key: string, label: string, count: number }>>([]);
    const [viewMode, setViewMode] = useState<'graph' | 'table'>('graph');
    const cyRef = useRef<any>(null);
    const theme = useMantineTheme();

    // Extract all reference properties from entity
    const extractReferenceProperties = (entity: any): Array<{ key: string, label: string, count: number }> => {
        if (!entity) return [];

        // Blacklist properties that are not meaningful relationships
        const blacklistedProperties = [
            'EntityType', 'Type', '$type', 'entityType', 'type',
            'Id', 'id', '$id', 'DisplayName', 'displayName', 'Name', 'name',
            'UserName', 'userName', 'Email', 'email', 'Phone', 'phone',
            'Created', 'created', 'Modified', 'modified', 'Version', 'version',
            'Active', 'active', 'Enabled', 'enabled', 'Status', 'status'
        ];

        const referenceProperties: Array<{ key: string, label: string, count: number }> = [];

        Object.entries(entity).forEach(([key, value]) => {
            // Skip blacklisted properties
            if (blacklistedProperties.includes(key)) {
                return;
            }

            if (value && (Array.isArray(value) || (typeof value === 'object' && value !== null))) {
                let count = 0;
                if (Array.isArray(value)) {
                    count = value.length;
                } else if (value && typeof value === 'object') {
                    count = 1;
                }

                if (count > 0) {
                    referenceProperties.push({
                        key,
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        count
                    });
                }
            }
        });

        // For workflow activities, extract entity selector types from config/settings.props
        const entityTypeStr = formatEntityType(entity.EntityType || entity.Type);
        const configValue = (entity?.Settings ?? (entity as any)?.settings);
        if (entityTypeStr?.toLowerCase() === 'workflowactivity' && configValue != null) {
            try {
                const configObj = typeof configValue === 'string' ? JSON.parse(configValue) : configValue;
                if (configObj && configObj.props && Array.isArray(configObj.props)) {
                    configObj.props.forEach((prop: any) => {
                        if (prop.controlType && prop.controlType.includes('entitySelector') && prop.entityType) {
                            const propName = prop.name || 'unnamed';
                            const controlType = prop.controlType;
                            const label = `${propName} (${controlType})`;

                            // Check if this property already exists
                            const existingIndex = referenceProperties.findIndex(p => p.key === label);
                            if (existingIndex >= 0) {
                                referenceProperties[existingIndex].count += 1;
                            } else {
                                referenceProperties.push({
                                    key: label,
                                    label: label,
                                    count: 1
                                });
                            }
                        }
                    });
                }
            } catch (e) {
                console.warn('Failed to parse Config for entity selector extraction:', e);
            }
        }

        return referenceProperties.sort((a, b) => b.count - a.count);
    };

    // Extract reference data from entity
    const extractReferences = (entity: any, propertyFilter?: string) => {
        const nodes: GraphNode[] = [];
        const edges: GraphEdge[] = [];
        const nodeMap = new Map<string, GraphNode>();

        if (!entity) return { nodes, edges };

        // Add main entity as central node
        const mainEntityId = entity.Id || entity.id || 'main-entity';
        const mainEntityLabel = String(entity.DisplayName || entity.Name || entity.UserName || 'Main Entity');
        const mainEntityType = formatEntityType(entity.EntityType || entity.Type);

        const mainNode: GraphNode = {
            data: {
                id: mainEntityId,
                label: mainEntityLabel,
                type: 'main',
                entityType: mainEntityType,
                isMainEntity: true
            },
            position: { x: 0, y: 0 }
        };

        nodes.push(mainNode);
        nodeMap.set(mainEntityId, mainNode);

        // Get reference fields to process based on filter
        let referenceFields: string[] = [];

        if (propertyFilter && propertyFilter !== 'all') {
            // Filter to specific property
            referenceFields = [propertyFilter];
        } else {
            // Use all available reference properties
            const availableProps = extractReferenceProperties(entity);
            referenceFields = availableProps.map(prop => prop.key);
        }

        let nodeCounter = 0;

        // Handle entity selector types from config/settings.props for workflow activities
        const entityTypeStr = formatEntityType(entity.EntityType || entity.Type);
        const configValue = (entity?.Settings ?? (entity as any)?.settings);
        if (entityTypeStr?.toLowerCase() === 'workflowactivity' && configValue != null) {
            try {
                const configObj = typeof configValue === 'string' ? JSON.parse(configValue) : configValue;
                if (configObj && configObj.props && Array.isArray(configObj.props)) {
                    configObj.props.forEach((prop: any) => {
                        if (prop.controlType && prop.controlType.includes('entitySelector') && prop.entityType) {
                            const propName = prop.name || 'unnamed';
                            const controlType = prop.controlType;
                            const fieldName = `${propName} (${controlType})`;

                            // Only process if this field is in the filter (or filter is 'all')
                            if (propertyFilter && propertyFilter !== 'all' && propertyFilter !== fieldName) {
                                return;
                            }

                            // Create a reference node for the entity type
                            // Use a unique ID per prop to allow multiple props with same entity type
                            const refId = `entity-type-${prop.entityType}-${propName}-${nodeCounter++}`;
                            const refLabel = prop.entityType;
                            const refType = formatEntityType(prop.entityType);

                            // Check if we already have a node for this entity type (but allow multiple edges)
                            const existingNodeId = Array.from(nodeMap.keys()).find(id =>
                                id.startsWith(`entity-type-${prop.entityType}-`) &&
                                nodeMap.get(id)?.data.entityType === refType
                            );

                            const nodeIdToUse = existingNodeId || refId;

                            if (!nodeMap.has(nodeIdToUse)) {
                                const refNode: GraphNode = {
                                    data: {
                                        id: nodeIdToUse,
                                        label: refLabel,
                                        type: 'reference',
                                        entityType: refType
                                    },
                                    position: {
                                        x: Math.cos(nodeCounter * 0.5) * 200,
                                        y: Math.sin(nodeCounter * 0.5) * 200
                                    }
                                };
                                nodes.push(refNode);
                                nodeMap.set(nodeIdToUse, refNode);
                            }

                            // Add edge
                            const edgeId = `${mainEntityId}-${nodeIdToUse}-${fieldName}`;
                            edges.push({
                                data: {
                                    id: edgeId,
                                    source: mainEntityId,
                                    target: nodeIdToUse,
                                    label: fieldName,
                                    relationship: fieldName
                                }
                            });
                        }
                    });
                }
            } catch (e) {
                console.warn('Failed to parse Config for entity selector extraction:', e);
            }
        }

        referenceFields.forEach(fieldName => {
            const fieldValue = entity[fieldName];

            if (!fieldValue) return;

            // Handle single reference
            if (fieldValue && !Array.isArray(fieldValue)) {
                const refId = fieldValue.Id || fieldValue.id || fieldValue.$ref || `ref-${fieldName}-${nodeCounter++}`;
                const refLabel = String(fieldValue.DisplayName || fieldValue.Name || fieldValue.UserName || getReferenceDisplay(fieldValue) || fieldName);
                const refType = formatEntityType(fieldValue.EntityType || fieldValue.Type || 'Reference');

                if (!nodeMap.has(refId)) {
                    const refNode: GraphNode = {
                        data: {
                            id: refId,
                            label: refLabel,
                            type: 'reference',
                            entityType: refType
                        },
                        position: {
                            x: Math.cos(nodeCounter * 0.5) * 200,
                            y: Math.sin(nodeCounter * 0.5) * 200
                        }
                    };
                    nodes.push(refNode);
                    nodeMap.set(refId, refNode);
                }

                // Add edge
                const edgeId = `${mainEntityId}-${refId}-${fieldName}`;
                edges.push({
                    data: {
                        id: edgeId,
                        source: mainEntityId,
                        target: refId,
                        label: fieldName,
                        relationship: fieldName
                    }
                });
            }
            // Handle array of references
            else if (Array.isArray(fieldValue)) {
                fieldValue.forEach((refItem, index) => {
                    const refId = refItem.Id || refItem.id || refItem.$ref || `ref-${fieldName}-${index}-${nodeCounter++}`;
                    const refLabel = String(refItem.DisplayName || refItem.Name || refItem.UserName || getReferenceDisplay(refItem) || `${fieldName} ${index + 1}`);
                    const refType = formatEntityType(refItem.EntityType || refItem.Type || 'Reference');

                    if (!nodeMap.has(refId)) {
                        const refNode: GraphNode = {
                            data: {
                                id: refId,
                                label: refLabel,
                                type: 'reference',
                                entityType: refType
                            },
                            position: {
                                x: Math.cos(nodeCounter * 0.3) * 300,
                                y: Math.sin(nodeCounter * 0.3) * 300
                            }
                        };
                        nodes.push(refNode);
                        nodeMap.set(refId, refNode);
                    }

                    // Add edge
                    const edgeId = `${mainEntityId}-${refId}-${fieldName}-${index}`;
                    edges.push({
                        data: {
                            id: edgeId,
                            source: mainEntityId,
                            target: refId,
                            label: fieldName,
                            relationship: fieldName
                        }
                    });
                });
            }
        });

        return { nodes, edges };
    };

    // Process entity data when entity changes
    useEffect(() => {
        if (!entity) {
            setGraphData({ nodes: [], edges: [] });
            setAvailableReferenceProperties([]);
            return;
        }

        // Extract available reference properties
        const referenceProps = extractReferenceProperties(entity);
        setAvailableReferenceProperties(referenceProps);

        // Reset to 'all' if current selection is not available
        if (selectedReferenceProperty !== 'all' && !referenceProps.find(prop => prop.key === selectedReferenceProperty)) {
            setSelectedReferenceProperty('all');
        }
    }, [entity, selectedReferenceProperty]);

    useEffect(() => {
        if (!entity) {
            setGraphData({ nodes: [], edges: [] });
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { nodes, edges } = extractReferences(entity, selectedReferenceProperty);
            setGraphData({ nodes, edges });
        } catch (err) {
            console.error('Error extracting references:', err);
            setError('Failed to process entity references');
        } finally {
            setIsLoading(false);
        }
    }, [entity, selectedReferenceProperty]);

    // Cleanup Cytoscape instance to prevent notify errors
    useEffect(() => {
        return () => {
            if (cyRef.current) {
                try {
                    // Remove all event listeners to prevent notify errors
                    cyRef.current.removeAllListeners();
                    // Destroy the instance
                    cyRef.current.destroy();
                } catch (error) {
                    console.warn('Error cleaning up Cytoscape instance:', error);
                }
                cyRef.current = null;
            }
        };
    }, []);

    // Cytoscape layout configuration - using more stable layout
    const layout = {
        name: 'breadthfirst',        // More stable than 'cose'
        animate: false,              // Disable animation to prevent notify errors
        directed: true,
        spacingFactor: 1.5,
        padding: 50,
        fit: true,
        avoidOverlap: true,
        nodeDimensionsIncludeLabels: true
    };

    // Cytoscape stylesheet
    const stylesheet = [
        {
            selector: 'node',
            style: {
                'background-color': '#666',
                'label': 'data(label)',
                'text-valign': 'center',
                'text-halign': 'center',
                'color': 'white',
                'text-outline-width': 2,
                'text-outline-color': '#000',
                'font-size': '14px',
                'width': '80px',
                'height': '80px'
            }
        },
        {
            selector: 'node[type="main"]',
            style: {
                'background-color': '#1976d2',
                'width': '100px',
                'height': '100px',
                'font-size': '16px',
                'font-weight': 'bold'
            }
        },
        {
            selector: 'node[type="reference"]',
            style: {
                'background-color': '#4caf50',
                'width': '70px',
                'height': '70px',
                'font-size': '12px'
            }
        },
        {
            selector: 'edge',
            style: {
                'width': 2,
                'line-color': '#999',
                'target-arrow-color': '#999',
                'target-arrow-shape': 'triangle',
                'curve-style': 'bezier',
                'label': 'data(label)',
                'font-size': '10px',
                'text-rotation': 'autorotate',
                'text-margin-y': -10
            }
        }
    ];

    // Build Select data with counts (Mantine Select uses { value, label } format)
    const referenceSelectData = [
        {
            value: 'all',
            label: `All References (${availableReferenceProperties.reduce((sum, prop) => sum + prop.count, 0)})`
        },
        ...availableReferenceProperties.map((prop) => ({
            value: prop.key,
            label: `${prop.label} (${prop.count})`
        }))
    ];

    if (isLoading) {
        return (
            <LoadingState variant="centered" minHeight={400} label="Processing references..." />
        );
    }

    if (error) {
        return (
            <Alert color="red" m="md">
                {error}
            </Alert>
        );
    }

    if (graphData.nodes.length === 0) {
        return (
            <Box p="lg" ta="center">
                <Text size="sm" c="dimmed">
                    No references found for this entity.
                </Text>
            </Box>
        );
    }

    // Use fallback if Cytoscape is not available
    if (!CytoscapeComponent || typeof CytoscapeComponent !== 'function') {
        return (
            <Box w="100%" style={{ height: 'calc(90vh - 200px)' }}>
                <Box
                    style={{
                        height: '100%',
                        border: '1px solid var(--mantine-color-default-border)',
                        borderRadius: theme.radius.md
                    }}
                >
                    <FallbackGraph nodes={graphData.nodes} edges={graphData.edges} />
                </Box>
            </Box>
        );
    }

    // Safe Cytoscape wrapper that prevents runtime errors
    const SafeCytoscapeWrapper = () => {
        if (!CytoscapeComponent) {
            debugVerboseLog('🔍 ReferencesGraph: Using fallback - Cytoscape component not available');
            return <FallbackGraph nodes={graphData.nodes} edges={graphData.edges} />;
        }

        try {
            debugVerboseLog('🔍 ReferencesGraph: Rendering Cytoscape graph with', graphData.nodes.length, 'nodes and', graphData.edges.length, 'edges');
            return (
                <CytoscapeComponent
                    elements={[...graphData.nodes, ...graphData.edges]}
                    style={{ width: '100%', height: '100%' }}
                    layout={layout}
                    stylesheet={stylesheet}
                    cy={(cy: any) => {
                        if (cy) {
                            debugVerboseLog('🔍 ReferencesGraph: Cytoscape instance created successfully');

                            // Store the cy instance for cleanup
                            cyRef.current = cy;

                            // Add error handling for cytoscape events
                            cy.on('error', (event: any) => {
                                console.error('Cytoscape error:', event);
                            });

                            // Set initial zoom level for better viewing
                            cy.ready(() => {
                                try {
                                    // Fit the graph with padding
                                    cy.fit(null, 50);

                                    // Set a reasonable zoom level
                                    const currentZoom = cy.zoom();
                                    if (currentZoom > 1.2) {
                                        cy.zoom(1.2);
                                    } else if (currentZoom < 0.4) {
                                        cy.zoom(0.4);
                                    }

                                    debugVerboseLog('🔍 ReferencesGraph: Initial zoom set to:', cy.zoom());
                                } catch (error) {
                                    console.warn('Error setting initial zoom:', error);
                                }
                            });
                        }
                    }}
                />
            );
        } catch (error) {
            console.error('Cytoscape render error:', error);
            return <FallbackGraph nodes={graphData.nodes} edges={graphData.edges} />;
        }
    };

    return (
        <Box w="100%" style={{ height: 'calc(90vh - 150px)' }}>
            {/* Reference Property Selector and View Toggle */}
            {availableReferenceProperties.length > 0 && (
                <Box
                    mb="md"
                    p="md"
                    bg="var(--mantine-color-default)"
                    style={{
                        borderRadius: theme.radius.md,
                        border: '1px solid var(--mantine-color-default-border)'
                    }}
                >
                    <Group justify="space-between" align="center" wrap="wrap" gap="md">
                        <Group align="center" wrap="wrap" gap="md">
                            <Text size="sm" fw={500} c="dimmed">
                                Show References:
                            </Text>
                            <Select
                                size="sm"
                                label="Reference Type"
                                placeholder="Reference Type"
                                data={referenceSelectData}
                                value={selectedReferenceProperty}
                                onChange={(val) => val && setSelectedReferenceProperty(val)}
                                miw={200}
                            />
                        </Group>

                        {/* View Mode Toggle */}
                        <Group align="center" gap="xs">
                            <Text size="sm" fw={500} c="dimmed">
                                View:
                            </Text>
                            <SegmentedControl
                                value={viewMode}
                                onChange={(val) => setViewMode(val as 'graph' | 'table')}
                                size="sm"
                                data={[
                                    {
                                        value: 'graph',
                                        label: (
                                            <Tooltip label="Graph View">
                                                <Group gap={4}>
                                                    <IconBinaryTree size={16} />
                                                    <Text size="xs">Graph</Text>
                                                </Group>
                                            </Tooltip>
                                        )
                                    },
                                    {
                                        value: 'table',
                                        label: (
                                            <Tooltip label="Table View">
                                                <Group gap={4}>
                                                    <IconTable size={16} />
                                                    <Text size="xs">Table</Text>
                                                </Group>
                                            </Tooltip>
                                        )
                                    }
                                ]}
                            />
                        </Group>
                    </Group>
                </Box>
            )}

            {/* Content Container */}
            <Box
                style={{
                    height: '100%',
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: theme.radius.md
                }}
            >
                {viewMode === 'graph' ? (
                    <SafeCytoscapeWrapper />
                ) : (
                    <FallbackGraph nodes={graphData.nodes} edges={graphData.edges} />
                )}
            </Box>
        </Box>
    );
};
